import { useCallback, useContext } from 'react';

import { ExternalOfferFragment } from '@ocx/graphql';
import type { AltriaOffersNearbyStatus } from '@ocx/feat-local-offers';
import { LocalOffersLocationContext } from '@ocx/feat-local-offers';
import { useLogOfferActivity } from '@ocx/data-offers';

import { ExternalBannerCard } from './external-banner-card.component';
import { useExternalBannerDetailsPopup } from '../external-banner-details-popup/external-banner-details-popup.hook';

export type ExternalBannerCardControllerProps = {
  banner: ExternalOfferFragment;
  hasAltriaOffersNearby?: AltriaOffersNearbyStatus;
};

export const ExternalBannerCardController = ({ banner, hasAltriaOffersNearby }: ExternalBannerCardControllerProps) => {
  const { open: openExternalBannerDetailsPopup } = useExternalBannerDetailsPopup();
  const { logClick, logImpression } = useLogOfferActivity();

  const contextData = useContext(LocalOffersLocationContext);
  const locationState = contextData?.locationState ?? null;

  const shouldShowUnavailableWarning = hasAltriaOffersNearby === 'not-available' && locationState !== 'all';

  const onBannerCardClick = useCallback(
    (trackingId: string) => {
      logClick(trackingId, banner.externalCampaignId, banner.bannerConfigurationUuid);
      openExternalBannerDetailsPopup({ banner, hasAltriaOffersNearby });
    },
    [banner, logClick, openExternalBannerDetailsPopup, hasAltriaOffersNearby],
  );

  const onImpression = useCallback(
    (offerId: string) => {
      logImpression(offerId, banner.externalCampaignId, banner.bannerConfigurationUuid);
    },
    [banner.bannerConfigurationUuid, banner.externalCampaignId, logImpression],
  );

  return (
    <ExternalBannerCard
      trackingId={banner.trackingId}
      content={banner.content}
      shouldShowUnavailableWarning={shouldShowUnavailableWarning}
      onImpression={onImpression}
      onClick={onBannerCardClick}
    />
  );
};
